import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { classNames } from "../../helpers/classNames";
import Button from "../form/Button";
import React, { cloneElement, useEffect, useRef, useState } from "react";
import { elementResize } from "../../helpers";

function isReactElement(value) {
  return React.isValidElement(value);
}

export default function Drawer({
  actualSize,
  size = "xl4",
  open = false,
  onClose = () => { },
  header,
  title = "Modal title",
  subtitle = "Modal subtitle",
  children,
  animationDuration = 0.2,
  headerClass,
  openFrom = "right",
  drawerBodyClass,
  closeBtn = true,
}) {
  const [sizeValue, setSizeValue] = useState(size);

  useEffect(() => {
    setSizeValue(size);
  }, [size]);

  const drawerSize = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    xl2: "max-w-2xl",
    xl3: "max-w-3xl",
    xl4: "max-w-4xl",
    xl5: "max-w-5xl",
    xl6: "max-w-6xl",
    xl7: "max-w-7xl",
    xl8: "max-w-8xl",
    xl9: "max-w-9xl",
    full: "max-w-full !rounded-none",
  };

  const getMotionProps = () => {
    if (sizeValue === "full") {
      return {
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 },
      };
    }

    switch (openFrom) {
      case "left":
        return {
          initial: { x: "-100%" },
          animate: { x: 0 },
          exit: { x: "-100%" },
        };
      case "top":
        return {
          initial: { y: "-100%" },
          animate: { y: 0 },
          exit: { y: "-100%" },
        };
      case "bottom":
        return {
          initial: { y: "100%" },
          animate: { y: 0 },
          exit: { y: "100%" },
        };
      case "right":
      default:
        return {
          initial: { x: "100%" },
          animate: { x: 0 },
          exit: { x: "100%" },
        };
    }
  };

  const positionClasses = () => {
    if (sizeValue === "full") return "justify-center items-center"; // center popup

    switch (openFrom) {
      case "left":
        return "justify-start";
      case "right":
        return "justify-end";
      case "top":
        return "items-start";
      case "bottom":
        return "items-end";
      default:
        return "justify-end";
    }
  };

  const isHorizontal = openFrom === "left" || openFrom === "right";

  const renderChildrenWithProps = () =>
    React.Children.map(children, (child) =>
      isReactElement(child) && typeof child.type !== "string"
        ? cloneElement(child, {
          actualSize,
          sizeValue,
          open,
          onClose,
          setSizeValue,
          header,
          heading: title,
          subtitle,
          children,
          animationDuration,
          headerClass,
          openFrom,
          drawerBodyClass,
          closeBtn,
        })
        : child
    );

  const renderHeaderWithProps = () =>
    React.Children.map(isReactElement(header) ? header : <></>, (child) =>
      isReactElement(child) && typeof child.type !== "string"
        ? cloneElement(child, {
          actualSize,
          sizeValue,
          open,
          onClose,
          setSizeValue,
          header,
          heading: title,
          subtitle,
          children,
          animationDuration,
          headerClass,
          openFrom,
          drawerBodyClass,
          closeBtn,
        })
        : child
    );

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          as="div"
          className={classNames(
            "relative",
            sizeValue === "full" || openFrom === "top" || openFrom === "bottom"
              ? "z-90"
              : "z-50"
          )}
          onClose={onClose}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30"
            aria-hidden="true"
          />

          <div className={classNames("fixed inset-0 flex", positionClasses())}>
            <motion.div
              {...getMotionProps()}
              transition={{ duration: animationDuration }}
              className={classNames(
                "bg-white shadow-2xl overflow-hidden",
                isHorizontal ? "h-full w-full rounded-l-xl" : "w-full",
                !isHorizontal ? "w-full" : "",
                openFrom === "top" || openFrom === "bottom"
                  ? drawerSize["full"]
                  : drawerSize[sizeValue]
              )}
            >
              <DialogPanel
                as={motion.div}
                {...getMotionProps()}
                transition={{ type: "tween" }}
                className="h-full w-full"
              >
                <div className="w-full h-full flex flex-col">
                  {header !== false && (
                    <div className="relative flex-grow-0">
                      {header ? (
                        renderHeaderWithProps()
                      ) : (
                        <div className="p-6 py-4 bg-gray-100 border-b border-gray-300">
                          <div className={classNames("space-y-1", headerClass)}>
                            {title && (
                              <DialogTitle className="font-bold text-xl">
                                {title}
                              </DialogTitle>
                            )}
                            {subtitle && (
                              <Description className="text-sm text-gray-400">
                                {subtitle}
                              </Description>
                            )}
                          </div>
                        </div>
                      )}
                      {closeBtn && (
                        <Button
                          buttonIconPosition="left"
                          buttonIcon="fa-regular fa-xmark"
                          buttonClasses="absolute top-3 right-3 p-2 h-8 w-8 !bg-none !text-slate-400 hover:!bg-slate-600/10 hover:!text-slate-600"
                          buttonFunction={onClose}
                        />
                      )}
                    </div>
                  )}
                  <div
                    className={classNames(
                      "flex-grow w-full p-6",
                      header ? "max-h-[calc(100%-85px)]" : "max-h-full",
                      drawerBodyClass
                    )}
                  >
                    {renderChildrenWithProps()}
                  </div>
                </div>
              </DialogPanel>
            </motion.div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
